import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, RefreshCw, ChevronLeft, ChevronRight, Volume2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts flashcard data from various backend shapes (graceful fallback).
 * @param {Object} lesson
 * @returns {Array<{id, front, back, example}>}
 */
function getFlashcards(lesson) {
  const raw =
    lesson?.content?.flashcards ||
    lesson?.flashcards ||
    lesson?.content?.cards ||
    lesson?.cards ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((c, i) => ({
      id: c.id || i,
      front: c.front || c.word || c.term || c.question || '',
      back: c.back || c.translation || c.definition || c.answer || '',
      example: c.example || c.example_sentence || '',
    }));
  }
  return null; // signals "no content"
}

/**
 * FlashcardLesson — 3D flip kelime ezberi bileşeni (lesson_type === 'flashcard').
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.flashcards: [{ front, back, example }]
 *
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function FlashcardLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawCards = getFlashcards(lesson);
  const hasContent = rawCards !== null && rawCards.length > 0;
  const initialCards = hasContent ? rawCards : [];

  const [deck, setDeck] = useState(initialCards);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownIds, setKnownIds] = useState(new Set());
  const [unknownIds, setUnknownIds] = useState(new Set());
  const [finished, setFinished] = useState(false);
  const [completing, setCompleting] = useState(false);

  const total = initialCards.length;
  const current = deck[currentIndex];
  const progress = total > 0 ? Math.round(((knownIds.size + unknownIds.size) / total) * 100) : 0;

  const flip = () => setIsFlipped(f => !f);

  const speakText = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    window.speechSynthesis.speak(utt);
  }, []);

  const handleKnown = () => {
    setKnownIds(prev => new Set([...prev, current.id]));
    advance();
  };

  const handleUnknown = () => {
    setUnknownIds(prev => new Set([...prev, current.id]));
    // Move card to end of deck so it comes back
    setDeck(prev => {
      const rest = prev.filter((_, i) => i !== currentIndex);
      return [...rest, prev[currentIndex]];
    });
    // Stay at same index (next card slides in)
    setIsFlipped(false);
    if (currentIndex >= deck.length - 1) {
      // was last card but it's been appended — check if we've seen all original
      checkFinished(knownIds);
    }
  };

  const advance = () => {
    setIsFlipped(false);
    if (currentIndex < deck.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      checkFinished(new Set([...knownIds, current.id]));
    }
  };

  const checkFinished = (updatedKnown) => {
    if (updatedKnown.size >= total) {
      setFinished(true);
    } else {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
      setIsFlipped(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < deck.length - 1) {
      setCurrentIndex(i => i + 1);
      setIsFlipped(false);
    }
  };

  const restart = () => {
    setDeck(initialCards);
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownIds(new Set());
    setUnknownIds(new Set());
    setFinished(false);
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const score = total > 0 ? Math.round((knownIds.size / total) * 100) : 0;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="flashcard-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="flashcard-empty-state">
        <CheckCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz flashcard içeriği tanımlanmamış.' : 'No flashcard content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="flashcard-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <FlashcardUI
      deck={deck}
      currentIndex={currentIndex}
      isFlipped={isFlipped}
      knownIds={knownIds}
      unknownIds={unknownIds}
      total={total}
      progress={progress}
      finished={finished}
      completing={completing}
      reduced={reduced}
      t={t}
      lang={lang}
      onFlip={flip}
      onKnown={handleKnown}
      onUnknown={handleUnknown}
      onPrev={handlePrev}
      onNext={handleNext}
      onRestart={restart}
      onComplete={handleComplete}
      onSpeak={speakText}
    />
  );
}

function FlashcardUI({
  deck, currentIndex, isFlipped, knownIds, unknownIds,
  total, progress, finished, completing, reduced,
  t, lang, onFlip, onKnown, onUnknown, onPrev, onNext,
  onRestart, onComplete, onSpeak,
}) {
  const current = deck[currentIndex];

  // Finished screen
  if (finished) {
    const score = total > 0 ? Math.round((knownIds.size / total) * 100) : 0;
    return (
      <motion.div
        initial={reduced ? {} : { opacity: 0, scale: 0.9 }}
        animate={reduced ? {} : { opacity: 1, scale: 1 }}
        className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 p-8 text-center"
        data-testid="flashcard-completed"
      >
        <div className="w-20 h-20 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={40} className="text-white" />
        </div>
        <h2 className="font-display text-3xl font-bold text-cyan-700 mb-1">
          {t('flashcardLesson.completed')}
        </h2>
        <p className="text-gray-600 font-semibold mb-6">
          {knownIds.size}/{total} {t('flashcardLesson.wordCount')} — {t('flashcardLesson.score')}: %{score}
        </p>
        <div className="flex justify-center gap-4">
          <motion.button
            whileHover={reduced ? {} : { scale: 1.03 }}
            whileTap={reduced ? {} : { scale: 0.97 }}
            onClick={onComplete}
            disabled={completing}
            className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
            data-testid="flashcard-complete-btn"
          >
            <CheckCircle size={18} />
            {completing ? '...' : t('common.completeLesson')}
          </motion.button>
          <motion.button
            whileHover={reduced ? {} : { scale: 1.03 }}
            whileTap={reduced ? {} : { scale: 0.97 }}
            onClick={onRestart}
            className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
            data-testid="flashcard-restart-btn"
          >
            <RefreshCw size={16} /> {t('flashcardLesson.restart')}
          </motion.button>
        </div>
      </motion.div>
    );
  }

  if (!current) return null;

  return (
    <div data-testid="flashcard-lesson">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs font-bold text-gray-500 mb-1">
          <span>{t('flashcardLesson.progressLabel')}</span>
          <span>
            {Math.min(knownIds.size + unknownIds.size, total)}/{total} {t('flashcardLesson.wordCount')}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
          <motion.div
            className="h-full bg-cyan-500 rounded-full"
            animate={{ width: `${progress}%` }}
            transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 100 }}
          />
        </div>
        <div className="flex gap-3 mt-1 text-xs font-semibold">
          <span className="text-emerald-600">{t('flashcardLesson.knownCount')}: {knownIds.size}</span>
          <span className="text-rose-500">{t('flashcardLesson.unknownCount')}: {unknownIds.size}</span>
        </div>
      </div>

      {/* Card */}
      <div
        className="relative w-full mb-6"
        style={{ perspective: '1000px', minHeight: '260px' }}
        data-testid="flashcard-container"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${current.id}-${isFlipped ? 'back' : 'front'}`}
            initial={reduced ? {} : { rotateY: isFlipped ? -90 : 90, opacity: 0 }}
            animate={reduced ? {} : { rotateY: 0, opacity: 1 }}
            exit={reduced ? {} : { rotateY: isFlipped ? 90 : -90, opacity: 0 }}
            transition={reduced ? {} : { duration: 0.3 }}
            style={{ transformStyle: 'preserve-3d' }}
            className="w-full"
          >
            {!isFlipped ? (
              /* Front face */
              <button
                onClick={onFlip}
                aria-label={lang === 'tr' ? 'Kartı çevir' : 'Flip card'}
                className="w-full rounded-2xl border-2 border-cyan-300 bg-white shadow-[0_6px_0_#06b6d4] p-8 text-center cursor-pointer hover:shadow-[0_4px_0_#06b6d4] hover:translate-y-0.5 active:shadow-none active:translate-y-1.5 transition-all"
                data-testid="flashcard-front"
              >
                <p className="text-xs font-bold text-cyan-500 mb-4 uppercase tracking-widest">
                  {t('flashcardLesson.tapToFlip')}
                </p>
                <p className="font-display text-4xl font-bold text-gray-800 mb-2">
                  {current.front}
                </p>
                <p className="text-xs text-gray-400 font-semibold mt-4">
                  {currentIndex + 1} / {deck.length}
                </p>
              </button>
            ) : (
              /* Back face */
              <div
                className="w-full rounded-2xl border-2 border-emerald-300 bg-emerald-50 shadow-[0_6px_0_#059669] p-8 text-center"
                data-testid="flashcard-back"
              >
                <p className="font-display text-3xl font-bold text-emerald-700 mb-3">
                  {current.back}
                </p>
                {current.example && (
                  <p className="text-sm text-gray-600 italic mb-4 border-t border-emerald-200 pt-3">
                    "{current.example}"
                  </p>
                )}
                <button
                  onClick={() => onSpeak(current.front)}
                  aria-label={lang === 'tr' ? 'Sesi oynat' : 'Play audio'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-cyan-300 bg-white text-cyan-600 font-bold text-sm hover:bg-cyan-50 transition-colors"
                  data-testid="flashcard-speak-btn"
                >
                  <Volume2 size={16} /> {lang === 'tr' ? 'Dinle' : 'Listen'}
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <motion.button
          whileHover={reduced ? {} : { scale: 1.03 }}
          whileTap={reduced ? {} : { scale: 0.97 }}
          onClick={onPrev}
          disabled={currentIndex === 0}
          aria-label={t('flashcardLesson.prev')}
          className="p-3 rounded-xl border-2 border-gray-200 bg-white shadow-[0_3px_0_#d1d5db] active:shadow-none active:translate-y-0.5 transition-all disabled:opacity-30 text-gray-600"
          data-testid="flashcard-prev-btn"
        >
          <ChevronLeft size={20} />
        </motion.button>

        <div className="flex gap-3 flex-1 justify-center">
          <motion.button
            whileHover={reduced ? {} : { scale: 1.03 }}
            whileTap={reduced ? {} : { scale: 0.97 }}
            onClick={onUnknown}
            disabled={!isFlipped}
            className="flex-1 max-w-[150px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-rose-400 bg-white text-rose-500 font-bold shadow-[0_4px_0_#fb7185] active:shadow-none active:translate-y-1 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="flashcard-unknown-btn"
          >
            <ThumbsDown size={18} /> {t('flashcardLesson.iDontKnow')}
          </motion.button>
          <motion.button
            whileHover={reduced ? {} : { scale: 1.03 }}
            whileTap={reduced ? {} : { scale: 0.97 }}
            onClick={onKnown}
            disabled={!isFlipped}
            className="flex-1 max-w-[150px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="flashcard-known-btn"
          >
            <ThumbsUp size={18} /> {t('flashcardLesson.iKnow')}
          </motion.button>
        </div>

        <motion.button
          whileHover={reduced ? {} : { scale: 1.03 }}
          whileTap={reduced ? {} : { scale: 0.97 }}
          onClick={onNext}
          disabled={currentIndex >= deck.length - 1}
          aria-label={t('flashcardLesson.next')}
          className="p-3 rounded-xl border-2 border-gray-200 bg-white shadow-[0_3px_0_#d1d5db] active:shadow-none active:translate-y-0.5 transition-all disabled:opacity-30 text-gray-600"
          data-testid="flashcard-next-btn"
        >
          <ChevronRight size={20} />
        </motion.button>
      </div>
    </div>
  );
}
